/**
 * Canonical note-category keys (slugs) — the stable, language-neutral identifiers stored on a note.
 * Display labels come from i18n (see `NOTE_CATEGORY_MESSAGE_KEY`); never use these slugs for display.
 *
 * These slugs are PROVISIONAL until the backend ships an official category enum. They are the
 * lowercase/underscored form of the legacy PascalCase values that were stored before the key switch
 * (e.g. "Work Order"), so `normalizeNoteCategory` maps any already-saved note onto the matching slug
 * and old notes keep working. Align these with the backend contract once it exists.
 */
export type EntityNoteCategory =
  | 'connection'
  | 'defect'
  | 'inspection'
  | 'lubrication'
  | 'maintenance'
  | 'other'
  | 'repair'
  | 'replacement'
  | 'safety'
  | 'work_order';

/** Display order used by every category picker. */
export const NOTE_CATEGORY_KEYS: EntityNoteCategory[] = [
  'connection',
  'defect',
  'inspection',
  'lubrication',
  'maintenance',
  'other',
  'repair',
  'replacement',
  'safety',
  'work_order',
];

/** i18n message key (under `custom.`) for each category's localized label. */
export const NOTE_CATEGORY_MESSAGE_KEY: Record<EntityNoteCategory, string> = {
  connection: 'noteCategoryConnection',
  defect: 'noteCategoryDefect',
  inspection: 'noteCategoryInspection',
  lubrication: 'noteCategoryLubrication',
  maintenance: 'noteCategoryMaintenance',
  other: 'noteCategoryOther',
  repair: 'noteCategoryRepair',
  replacement: 'noteCategoryReplacement',
  safety: 'noteCategorySafety',
  work_order: 'noteCategoryWorkOrder',
};

/**
 * Normalize any stored category onto a canonical slug. Handles both new slugs and legacy values
 * (PascalCase / spaces, e.g. "Work Order" → "work_order") so notes created before the key switch
 * still resolve to a known category. Unknown values are slugified best-effort.
 */
export function normalizeNoteCategory(raw: string): EntityNoteCategory {
  return raw.trim().toLowerCase().replace(/\s+/g, '_') as EntityNoteCategory;
}
